import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BiMessageSquareDetail, BiSend, BiTrash, BiUserCircle } from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls.js";

// ✅ Function to generate avatar placeholder with initials (like UserList)
const getInitialsPlaceholder = (name = "") => {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(
    name
  )}&background=random&color=fff&size=40&bold=true`;
};

const CaseMessaging = ({ caseId, isPiCase = false }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPosting, setIsPosting] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");

  const API_ENDPOINT = isPiCase ? "pi-cases" : "cases";

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/${API_ENDPOINT}/${caseId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(res.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching messages:", err);
      setError("Failed to load messages.");
    } finally {
      setLoading(false);
    }
  }, [caseId, token, API_ENDPOINT]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handlePostMessage = async (e) => {
    e.preventDefault();
    if (newMessage.trim() === "" || isPosting) return;

    setIsPosting(true);
    try {
      await axios.post(
        `${API_BASE_URL}/${API_ENDPOINT}/${caseId}/messages`,
        { message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage("");
      await fetchMessages();
    } catch (err) {
      console.error("Error posting message:", err);
      alert("Failed to post message: " + (err.response?.data?.message || "Server error"));
    } finally {
      setIsPosting(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;

    try {
      await axios.delete(`${API_BASE_URL}/${API_ENDPOINT}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchMessages();
    } catch (err) {
      console.error("Error deleting message:", err);
      alert("Failed to delete message: " + (err.response?.data?.message || "Server error"));
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  return (
    <div className="bg-white p-5 rounded-lg shadow-lg border border-gray-100 mt-8 print:hidden">
      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
        <BiMessageSquareDetail className="w-6 h-6 mr-2 text-blue-600" />
        Case Messaging
      </h3>

      {/* Post Message Form */}
      <form onSubmit={handlePostMessage} className="mb-4">
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type your message to all case members..."
          rows="3"
          className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          disabled={isPosting}
        />
        <button
          type="submit"
          disabled={isPosting || newMessage.trim() === ""}
          className="w-full mt-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
        >
          <BiSend className="w-4 h-4 mr-2" />
          {isPosting ? "Posting..." : "Post Message"}
        </button>
      </form>

      {/* Message List */}
      <div className="max-h-96 overflow-y-auto space-y-3 pr-2">
        {loading && <p className="text-gray-500">Loading messages...</p>}
        {error && <p className="text-red-500">{error}</p>}

        {!loading && messages.length === 0 && (
          <p className="text-gray-500 text-sm text-center py-4">
            No messages have been posted to this case yet.
          </p>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className="relative p-3 bg-gray-50 rounded-md border border-gray-200 hover:shadow-sm transition"
          >
            {/* Header: Profile + Name + Time + Delete */}
            <div className="flex justify-between items-start mb-1">
              <div className="flex items-center space-x-2">
                {/* ✅ If profile_image missing → generate initials placeholder */}
                <img
                  src={
                    msg.profile_image ||
                    getInitialsPlaceholder(msg.user_name || "User")
                  }
                  alt={msg.user_name}
                  className="w-7 h-7 rounded-full object-cover"
                />
                <div className="flex flex-col">
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-gray-900 text-sm">
                      {msg.user_name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatTime(msg.created_at)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Delete Button (top-right) */}
              {currentUser && msg.user_id === currentUser.id && (
                <button
                  onClick={() => handleDeleteMessage(msg.id)}
                  className="text-red-500 hover:text-red-700 text-xs flex items-center"
                  title="Delete this message"
                >
                  <BiTrash className="w-4 h-4 mr-1" /> Delete
                </button>
              )}
            </div>

            {/* Message Text */}
            <p className="text-gray-700 text-sm whitespace-pre-wrap ml-9">
              {msg.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CaseMessaging;
